import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, MessageCircle, MapPin, User, Phone, Package, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useCart } from '@/context/CartContext';
import { cartItemToAnalytics, trackEcommerce } from '@/utils/analytics';

export function OrderSummaryPage() {
  const { state } = useCart();
  const checkoutTracked = useRef(false);

  useEffect(() => {
    if (checkoutTracked.current || state.items.length === 0) return;
    checkoutTracked.current = true;
    trackEcommerce('begin_checkout', state.items.map((item) => cartItemToAnalytics(item)));
  }, [state.items]);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    reference: '',
    deliveryMethod: 'entrega',
    notes: ''
  });

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN'
    }).format(price);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const generateWhatsAppMessage = () => {
    let message = "Hola, quiero confirmar este pedido:\n\n";
    
    // Products
    message += "📦 PRODUCTOS:\n";
    state.items.forEach((item, index) => {
      message += `${index + 1}. ${item.name}\n`;
      message += `   • Presentación: ${item.presentation}\n`;
      message += `   • Cantidad: ${item.quantity}\n`;
      message += `   • Precio unitario: ${formatPrice(item.unitPrice)}\n`;
      message += `   • Subtotal: ${formatPrice(item.subtotal)}\n\n`;
    });

    message += `💰 TOTAL: ${formatPrice(state.total)}\n\n`;

    // Customer info
    message += "👤 DATOS DEL CLIENTE:\n";
    message += `• Nombre: ${formData.name}\n`;
    message += `• Teléfono: ${formData.phone}\n`;
    message += `• Dirección: ${formData.address}\n`;
    if (formData.reference) {
      message += `• Referencia: ${formData.reference}\n`;
    }
    message += `• Método: ${formData.deliveryMethod === 'entrega' ? 'Entrega a domicilio' : 'Recojo en tienda'}\n`;
    if (formData.notes) {
      message += `• Notas: ${formData.notes}\n`;
    }

    message += "\n¿Podrían confirmar la disponibilidad y el tiempo de entrega? ¡Gracias! 😊";

    return message;
  };

  const handleWhatsAppOrder = () => {
    if (!formData.name || !formData.phone || !formData.address) {
      alert('Por favor, completa todos los campos obligatorios');
      return;
    }

    trackEcommerce(
      'generate_lead',
      state.items.map((item) => cartItemToAnalytics(item)),
      { method: 'whatsapp', origen: 'resumen_pedido' },
    );

    const message = generateWhatsAppMessage();
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/+51915976931?text=${encodedMessage}`, '_blank');
  };

  if (state.items.length === 0) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-foreground mb-4">
            Tu carrito está vacío
          </h1>
          <p className="text-muted-foreground mb-6">
            Agrega algunos productos para realizar un pedido
          </p>
          <Button asChild>
            <Link to="/tienda">Explorar Productos</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button variant="ghost" asChild className="mb-4">
            <Link to="/tienda">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver a la tienda
            </Link>
          </Button>
          
          <h1 className="text-3xl lg:text-4xl font-bold text-foreground mb-2">
            Resumen del Pedido
          </h1>
          <p className="text-muted-foreground">
            Revisa tu pedido y completa tus datos para enviarlo por WhatsApp
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Order Items */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Package className="w-5 h-5 mr-2" />
                  Productos ({state.items.length} ítems)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {state.items.map((item, index) => (
                  <div key={`${item.productId}-${item.presentation}`} className="flex space-x-4 p-4 bg-muted/30 rounded-lg">
                    {/* Product Image */}
                    <div className="w-16 h-16 bg-muted rounded-lg flex-shrink-0 overflow-hidden">
                      {item.image ? (
                        <img 
                          src={item.image} 
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-primary/20 flex items-center justify-center">
                          <span className="text-xs text-primary font-medium">
                            {item.name.charAt(0)}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Product Info */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-foreground">{item.name}</h4>
                      <p className="text-sm text-muted-foreground">{item.presentation}</p>
                      <div className="flex justify-between items-center mt-2">
                        <span className="text-sm">Cantidad: {item.quantity}</span>
                        <div className="text-right">
                          <p className="text-sm text-muted-foreground">
                            {formatPrice(item.unitPrice)} c/u
                          </p>
                          <p className="font-semibold text-primary">
                            {formatPrice(item.subtotal)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Total */}
                <div className="border-t border-border pt-4">
                  <div className="flex justify-between items-center text-xl font-bold">
                    <span>Total:</span>
                    <span className="text-primary">{formatPrice(state.total)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Customer Form */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <User className="w-5 h-5 mr-2" />
                  Datos de Contacto
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nombre completo *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="Tu nombre completo"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Teléfono / WhatsApp *</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      placeholder="+51 999 999 999"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <MapPin className="w-5 h-5 mr-2" />
                  Dirección de Entrega
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="address">Dirección de entrega (Lima Este) *</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    placeholder="Ej: Calle Los Nogales 123, Ñaña"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reference">Referencia</Label>
                  <Input
                    id="reference"
                    value={formData.reference}
                    onChange={(e) => handleInputChange('reference', e.target.value)}
                    placeholder="Ej: Casa azul, frente al parque"
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <CreditCard className="w-5 h-5 mr-2" />
                  Método de Entrega
                </CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup 
                  value={formData.deliveryMethod} 
                  onValueChange={(value) => handleInputChange('deliveryMethod', value)}
                >
                  <div className="flex items-center space-x-2 p-3 border border-border rounded-lg">
                    <RadioGroupItem value="entrega" id="entrega" />
                    <Label htmlFor="entrega" className="flex-1 cursor-pointer">
                      <div className="font-medium">Entrega a domicilio</div>
                      <div className="text-sm text-muted-foreground">
                        Entregamos en Ñaña, Chaclacayo y Chosica (costo de envío a coordinar)
                      </div>
                    </Label>
                  </div>
                  
                  <div className="flex items-center space-x-2 p-3 border border-border rounded-lg">
                    <RadioGroupItem value="recojo" id="recojo" />
                    <Label htmlFor="recojo" className="flex-1 cursor-pointer">
                      <div className="font-medium">Recojo en tienda</div>
                      <div className="text-sm text-muted-foreground">
                        Coordinaremos punto de encuentro en Ñaña - Chaclacayo
                      </div>
                    </Label>
                  </div>
                </RadioGroup>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Notas adicionales</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Label htmlFor="notes">Comentarios (opcional)</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    placeholder="Algún comentario especial sobre tu pedido..."
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Submit Button */}
            <Button 
              onClick={handleWhatsAppOrder}
              className="w-full bg-primary hover:bg-primary-light text-lg py-3"
              size="lg"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Solicitar por WhatsApp
            </Button>

            <p className="text-sm text-muted-foreground text-center">
              Al hacer clic, se abrirá WhatsApp con un mensaje prellenado con los detalles de tu pedido. 
              Nuestro equipo te confirmará la disponibilidad y coordinará la entrega.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}